import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { useQueries } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import Select from 'react-select';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import {
  ValueComparisonCondition,
  RESOURCE_OPTIONS,
  FISH_RESOURCE,
  CUSTOM_FORM_RESOURCE,
} from '../ValueComparisonCondition';
import type {
  CustomFormFieldOption,
  CustomFormFieldType,
} from '../ValueComparisonCondition';
import { buildFieldConfigs } from '../DynamicCustomForm/utils/json-schema-to-fields';
import { parseJsonSafe } from '../DynamicCustomForm/utils/parse-json-safe';
import type {
  CustomFormData,
  FieldType,
  JsonSchema,
  UiSchema,
} from '../DynamicCustomForm/types';
import service from '../../service';
import type {
  StageConditions,
  StageConditionsFormValues,
  StageValueComparison,
  UpcomingSchedule,
  UpcomingStageSettings,
} from '../../types/flowStage';
import { UpcomingScheduleSection } from './UpcomingScheduleSection';

interface StageConditionsEditorProps {
  flowStageType?: { name: string; upcoming?: UpcomingStageSettings | null } | null;
  initialConditions?: StageConditions | null;
  onChange: (conditions: StageConditions | null) => void;
  canWrite?: boolean;
  // 'stacked' = original sidebar column; 'grouped' = drawer resource-grouped cards.
  variant?: 'stacked' | 'grouped';
  // Read-only natural-language summary shown alongside the Add-resource control.
  summary?: string | null;
  // Ids of the custom forms feeding this stage; non-empty marks a custom-form stage.
  customFormIds?: string[];
}

// Custom-form questions usable as conditions: only those with a known value set.
const CUSTOM_FORM_FIELD_TYPES: Partial<Record<FieldType, CustomFormFieldType>> = {
  select: 'string',
  radio: 'string',
  checkbox: 'boolean',
  date: 'date',
  'datetime-local': 'date',
};

// Event rows 500 on form stage types and activity has no context on the
// custom-form submission path, so custom-form stages offer only these.
const CUSTOM_FORM_STAGE_RESOURCE_OPTIONS = [FISH_RESOURCE, CUSTOM_FORM_RESOURCE];

const ALL_RESOURCE_OPTIONS = [...RESOURCE_OPTIONS, CUSTOM_FORM_RESOURCE];

const NO_CUSTOM_FORM_IDS: string[] = [];

// Stable reference so useQueries keeps the combined array identity between renders.
const selectCustomForms = (results: UseQueryResult<CustomFormData>[]) =>
  results.map((result) => result.data);

const countLabel = (n: number) => (n === 1 ? '1 condition' : `${n} conditions`);

const NEW_ROW = {
  resource: '',
  resourceField: '',
  operator: 'includes',
  value: [] as any,
};

// Rows are saved once a resource and field are chosen; the operator defaults to
// 'includes', so a row isn't silently dropped for an unset operator.
const completeRows = (values: any): StageValueComparison[] =>
  (values?.conditions?.valueComparisons ?? []).filter(
    (vc: any) => vc && vc.resource && vc.resourceField,
  );

// The stage's conditions object, or null when there is nothing to keep. An upcoming
// stage's schedule is kept even with no condition rows.
const buildConditions = (
  valueComparisons: StageValueComparison[],
  schedule: UpcomingSchedule | undefined,
): StageConditions | null => {
  if (valueComparisons.length === 0 && !schedule) return null;
  const conditions: StageConditions = { type: 'ValueComparison', valueComparisons };
  if (schedule) conditions.schedule = schedule;
  return conditions;
};

/**
 * Per-stage condition builder. Owns a local react-hook-form so it can reuse the
 * schema-driven ValueComparisonCondition rows, and syncs the assembled conditions
 * back up to the stage on every change. Mount this keyed by stage id so switching
 * stages re-seeds the form from that stage's saved conditions.
 */
export const StageConditionsEditor: React.FC<StageConditionsEditorProps> = ({
  flowStageType,
  initialConditions,
  onChange,
  canWrite = true,
  variant = 'stacked',
  summary,
  customFormIds = NO_CUSTOM_FORM_IDS,
}) => {
  const { control, watch, setValue, getValues } = useForm<StageConditionsFormValues>({
    defaultValues: {
      conditions: {
        valueComparisons: initialConditions?.valueComparisons ?? [],
      },
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'conditions.valueComparisons',
  });

  const upcoming = flowStageType?.upcoming ?? null;
  const [schedule, setSchedule] = useState<UpcomingSchedule | undefined>(
    initialConditions?.schedule,
  );

  // Keep the latest onChange and schedule without resubscribing the watcher on
  // every render. Only an upcoming stage carries a schedule.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const scheduleRef = useRef<UpcomingSchedule | undefined>(undefined);
  scheduleRef.current = upcoming ? schedule : undefined;

  // watch(callback) fires on every field change — including the nested Controller
  // selects inside ValueComparisonCondition and append/remove — so the stage stays
  // in sync as the user edits.
  useEffect(() => {
    const subscription = watch((value) => {
      onChangeRef.current(buildConditions(completeRows(value), scheduleRef.current));
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const handleScheduleChange = (next: UpcomingSchedule) => {
    setSchedule(next);
    scheduleRef.current = next;
    onChangeRef.current(buildConditions(completeRows(getValues()), next));
  };

  const scheduleSection = upcoming ? (
    <UpcomingScheduleSection
      settings={upcoming}
      value={schedule}
      onChange={handleScheduleChange}
      canWrite={canWrite}
    />
  ) : null;

  const isLapse = flowStageType?.name === 'LAPSE_DETECTED';
  const isCustomFormStage = customFormIds.length > 0;

  // Upcoming stages offer only the resources the sweep can filter on.
  let resourceOptions = RESOURCE_OPTIONS;
  if (isCustomFormStage) {
    resourceOptions = CUSTOM_FORM_STAGE_RESOURCE_OPTIONS;
  } else if (upcoming) {
    resourceOptions = RESOURCE_OPTIONS.filter((r) =>
      upcoming.conditionResources.includes(r.value),
    );
  }

  const customForms = useQueries({
    queries: customFormIds.map((id) => ({
      queryKey: ['customForm', id],
      queryFn: async (): Promise<CustomFormData> => {
        const res = await service.getCustomFormById(id);
        if (!res.ok) throw new Error(`Custom form ${id} could not be loaded`);
        return res.json();
      },
    })),
    combine: selectCustomForms,
  });

  // A form that failed to load or whose schema does not parse contributes no fields.
  const customFormFields = useMemo(
    () =>
      customForms.flatMap((form): CustomFormFieldOption[] => {
        if (!form) return [];
        try {
          const schema = parseJsonSafe<JsonSchema>(form.formSchemaJson);
          const uiSchema = parseJsonSafe<UiSchema>(form.uiSchemaJson) ?? {};
          return buildFieldConfigs(schema, uiSchema).flatMap((field) => {
            const type = CUSTOM_FORM_FIELD_TYPES[field.type];
            if (!type) return [];
            return [
              {
                customFormId: form.id,
                key: field.name,
                label:
                  customFormIds.length > 1
                    ? `${form.title}: ${field.label}`
                    : field.label,
                type,
                choices: field.options ?? [],
              },
            ];
          });
        } catch {
          return [];
        }
      }),
    [customForms, customFormIds.length],
  );

  // Live resource values so rows can be grouped by resource.
  const watched = useWatch({ control, name: 'conditions.valueComparisons' });

  if (variant === 'grouped') {
    // Group row indices by resource, preserving first-appearance order.
    const order: string[] = [];
    const groups: Record<string, { id: string; index: number }[]> = {};
    fields.forEach((field, index) => {
      const resource =
        (watched?.[index]?.resource ?? (field as any).resource ?? '') || '';
      if (!groups[resource]) {
        groups[resource] = [];
        order.push(resource);
      }
      groups[resource].push({ id: field.id, index });
    });

    const usedResources = new Set(order.filter(Boolean));
    const unusedResources = resourceOptions.filter((r) => !usedResources.has(r.value));

    const removeResource = (indices: number[]) => remove(indices);

    return (
      <div className="flex flex-col gap-4">
        {scheduleSection}
        {/* Summary (left) + Add resource (right) on one row. Selecting a resource
            option adds that resource group. */}
        <div className="flex items-start justify-between gap-4">
          <p className="flex-1 text-sm text-zinc-500">{summary}</p>
          <div className="w-48 shrink-0">
            <Select
              value={null}
              onChange={(selected) => {
                if (selected) append({ ...NEW_ROW, resource: (selected as any).value });
              }}
              options={unusedResources}
              placeholder={
                unusedResources.length ? 'Add resource…' : 'All resources added'
              }
              isDisabled={!canWrite || unusedResources.length === 0}
              isSearchable={false}
              menuPortalTarget={
                typeof document !== 'undefined' ? document.body : undefined
              }
              menuPosition="fixed"
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
            />
          </div>
        </div>

        {order.map((resource) => {
          const entries = groups[resource];
          const resourceLabel =
            ALL_RESOURCE_OPTIONS.find((r) => r.value === resource)?.label ||
            resource ||
            'Unset';
          const notCheckedHere =
            isCustomFormStage &&
            !!resource &&
            !CUSTOM_FORM_STAGE_RESOURCE_OPTIONS.some((r) => r.value === resource);

          return (
            <div
              key={resource || 'unset'}
              className="overflow-hidden rounded-xl border border-zinc-200 bg-white"
            >
              <div className="flex items-center justify-between gap-4 border-b border-[#CAAB6A]/30 bg-[#CAAB6A]/10 px-4 py-3">
                <div className="flex items-baseline gap-2">
                  <span className="text-base font-bold text-[#8a6a2c]">
                    {resourceLabel}
                  </span>
                  <span className="text-sm text-[#8a6a2c]/70">
                    {countLabel(entries.length)}
                  </span>
                  {notCheckedHere && (
                    <span className="text-sm text-zinc-500">
                      Not checked on custom form stages
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <button
                    type="button"
                    className="font-medium text-[#a5854a] hover:text-[#8a6a2c] disabled:opacity-40"
                    onClick={() => append({ ...NEW_ROW, resource })}
                    disabled={!canWrite}
                  >
                    + Add condition
                  </button>
                  <button
                    type="button"
                    className="text-zinc-400 hover:text-red-600 disabled:opacity-40"
                    onClick={() => removeResource(entries.map((e) => e.index))}
                    disabled={!canWrite}
                  >
                    Remove resource
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto px-4 py-3">
                <div className="min-w-[520px]">
                  <div className="grid grid-cols-[22px_minmax(0,1.1fr)_minmax(0,0.8fr)_minmax(0,1.5fr)_26px] gap-2 pb-1 text-[11px] tracking-wide text-zinc-400">
                    <div />
                    <div>FIELD</div>
                    <div>OPERATOR</div>
                    <div>VALUE</div>
                    <div />
                  </div>
                  {entries.map((entry, ri) => {
                    const row = watched?.[entry.index];
                    const formNoLongerOnStage =
                      row?.resource === CUSTOM_FORM_RESOURCE.value &&
                      !!row.customFormId &&
                      !customFormIds.includes(row.customFormId);

                    return formNoLongerOnStage ? (
                      <div
                        key={entry.id}
                        className="grid grid-cols-[22px_minmax(0,1fr)_26px] items-center gap-2 py-1"
                      >
                        <div className="text-xs text-zinc-400">{ri + 1}</div>
                        <div className="text-sm text-zinc-500">
                          Form no longer on this stage
                        </div>
                        <div className="text-center">
                          {canWrite && (
                            <button
                              type="button"
                              onClick={() => remove(entry.index)}
                              className="text-zinc-400 hover:text-red-500"
                              title="Remove condition"
                            >
                              &times;
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      <ValueComparisonCondition
                        key={entry.id}
                        control={control}
                        flowStageType={flowStageType}
                        index={entry.index}
                        displayIndex={ri + 1}
                        variant="row"
                        hideResource
                        onRemove={() => remove(entry.index)}
                        showRemove={canWrite}
                        customFormFields={customFormFields}
                        setValue={setValue}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 pb-4">
      <h2 className="font-bold">{isLapse ? 'Lapse Conditions' : 'Conditions'}</h2>
      {scheduleSection}

      {fields.map((field, index) => (
        <ValueComparisonCondition
          key={field.id}
          control={control}
          flowStageType={flowStageType}
          index={index}
          onRemove={() => remove(index)}
          showRemove={canWrite}
          autoScroll
          customFormFields={customFormFields}
          setValue={setValue}
        />
      ))}

      <Button
        variant="outline"
        type="button"
        className="mt-1 flex items-center gap-2 self-start"
        onClick={() => append({ ...NEW_ROW })}
        disabled={!canWrite}
      >
        <FontAwesomeIcon icon={faPlus} /> Add Condition
      </Button>
    </div>
  );
};

export default StageConditionsEditor;
